/**
 * โรงคลัง window: the bag (left) and the store (right) side by side, opened by talking to a warehouse keeper and closed
 * when the player walks out of reach (rules: packages/shared/src/systems/warehouse.ts).
 * Click or drag an item across to move it: a bag item moves every loose copy, a store slot comes back whole; Shift+click
 * moves one; right-click a store stack splits it in half. ตำลึง move with the amount box. Esc or ✕ closes.
 * It reads `warehouseOf(world)` and the progress and sends `wh*` commands; nothing here decides a rule.
 */
import {
  BAG_SIZE,
  WAREHOUSE_TEXT as T,
  itemName,
  itemOf,
  warehouseOf,
  type PlayerCommand,
  type World,
  type WorldEvent,
} from '@nine/shared';
import { iconEl, itemIcon } from '../render/icons';
import { closeButton, h } from './dom';

const CSS = `
#warehouseWin{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);z-index:40;width:min(860px,94vw);max-height:88vh;
 box-sizing:border-box;padding:14px 16px 12px;display:flex;flex-direction:column;gap:10px;pointer-events:auto;color:#f4f1ea;
 background:rgba(22,19,15,.9);border:1px solid #c9a24a;outline:1px solid rgba(201,162,74,.45);outline-offset:3px;border-radius:4px;
 font-size:13px;line-height:1.35}
html[data-blur='on'] #warehouseWin{backdrop-filter:blur(8px)}
#warehouseWin[hidden]{display:none!important}
#warehouseWin::before,#warehouseWin::after{content:'';position:absolute;width:8px;height:8px;background:#c9a24a;transform:rotate(45deg);top:-5px}
#warehouseWin::before{left:-5px}#warehouseWin::after{right:-5px}
#warehouseWin .wh-head{display:flex;align-items:center;gap:10px}
#warehouseWin .wh-head h3{margin:0;font-size:17px;color:#f4d08c;letter-spacing:.04em}
#warehouseWin .wh-head small{color:#d4cec3}
#warehouseWin .wh-head .x-close{margin-left:auto}
#warehouseWin .wh-cols{display:grid;grid-template-columns:minmax(0,2fr) minmax(0,3fr);gap:14px;min-height:0;overflow:auto}
#warehouseWin section{display:flex;flex-direction:column;gap:6px;min-width:0;padding:8px;border:1px solid rgba(244,241,234,.16);border-radius:6px;background:rgba(0,0,0,.18)}
#warehouseWin section.drop{border-color:#f4d08c;background:rgba(244,208,140,.08)}
#warehouseWin .wh-sh{display:flex;justify-content:space-between;align-items:baseline}
#warehouseWin .wh-sh h4{margin:0;font-size:14px}
#warehouseWin .wh-sh small{color:#d4cec3}
#warehouseWin .wh-grid{display:grid;grid-template-columns:repeat(auto-fill,40px);gap:4px}
#warehouseWin .wh-cell{position:relative;width:40px;height:40px;box-sizing:border-box;padding:0;border:1px solid rgba(244,241,234,.14);border-radius:4px;
 background:rgba(244,241,234,.04);display:grid;place-items:center;cursor:pointer;color:inherit}
#warehouseWin .wh-cell.empty{cursor:default;background:rgba(0,0,0,.22)}
#warehouseWin .wh-cell:not(.empty):hover{border-color:#f4d08c}
#warehouseWin .wh-cell.r-rare{border-color:#4f8fdc}#warehouseWin .wh-cell.r-epic{border-color:#d08a3a}#warehouseWin .wh-cell.r-legend{border-color:#d6412f}
#warehouseWin .wh-cell canvas{width:32px;height:32px;image-rendering:pixelated;pointer-events:none}
#warehouseWin .wh-cell b{position:absolute;right:2px;bottom:0;font-size:11px;text-shadow:0 1px 1px #000,0 0 2px #000}
#warehouseWin .wh-cell i{position:absolute;left:1px;top:0;font-size:10px;font-style:normal;color:#7ee0b2;text-shadow:0 1px 1px #000}
#warehouseWin .wh-foot{display:flex;flex-wrap:wrap;align-items:center;gap:8px}
#warehouseWin .wh-gold{display:flex;align-items:center;gap:6px}
#warehouseWin .wh-gold b{color:#f4d08c;font-weight:600}
#warehouseWin input{width:84px;padding:3px 6px;border-radius:4px;border:1px solid rgba(244,241,234,.26);background:rgba(0,0,0,.35);color:#f4f1ea;font:inherit}
#warehouseWin button.wh-btn{min-height:28px;padding:0 10px;cursor:pointer;border:1px solid rgba(244,241,234,.26);background:rgba(22,19,15,.6);color:#f4f1ea;border-radius:5px;font:inherit}
#warehouseWin button.wh-btn.main{border-color:#d6412f;background:#a3281f}
#warehouseWin button.wh-btn:hover{border-color:#f4d08c}
#warehouseWin .wh-hint{margin-left:auto;color:#d4cec3;font-size:12px}
@media (max-width:700px){#warehouseWin .wh-cols{grid-template-columns:1fr}}
`;

type From = 'bag' | 'store';

export class WarehouseUi {
  readonly root = h('div', { id: 'warehouseWin', hidden: true, role: 'dialog', ariaLabel: T.name });
  private readonly body = h('div', { class: 'wh-body' });
  private lastKey = '';
  private dirty = true;
  private drag: { from: From; key: string } | null = null;

  constructor(
    private readonly world: World,
    private readonly send: (c: PlayerCommand) => void,
  ) {
    const style = h('style', {}, CSS);
    document.head.append(style);
    this.root.addEventListener('pointerdown', (e) => e.stopPropagation());
    this.root.addEventListener('wheel', (e) => e.stopPropagation(), { passive: true });
    this.root.addEventListener('contextmenu', (e) => e.preventDefault());
    this.root.append(this.body);
    document.querySelector('#hud')?.append(this.root);
    // Esc closes the store before anything else hears it.
    window.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Escape' || !this.isOpen) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.send({ t: 'whClose' });
        this.hide();
      },
      true,
    );
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  handle(ev: WorldEvent): void {
    if (ev.type === 'warehouse') {
      if (ev.what === 'open') this.show();
      else if (ev.what === 'close') this.hide();
      this.dirty = true;
    } else if (ev.type === 'progressChanged') this.dirty = true;
  }

  private show(): void {
    this.root.hidden = false;
    this.dirty = true;
    this.lastKey = '';
    // The keeper's line box would sit under the window; the window says it all.
    const dlg = document.querySelector<HTMLElement>('#dialog');
    if (dlg) dlg.hidden = true;
  }

  private hide(): void {
    this.root.hidden = true;
    this.drag = null;
  }

  /** Per frame: follows the world (a closed system closes the window) and redraws when the contents changed. */
  sync(): void {
    const sys = warehouseOf(this.world);
    if (this.isOpen && sys && !sys.isOpen) this.hide();
    if (!this.isOpen) return;
    const pr = this.world.progress;
    const st = sys?.view();
    const key = `${pr.gold}|${pr.inventory.join(',')}|${JSON.stringify(pr.equipment)}|${st ? `${st.gold}|${st.size}|${st.items.map((s) => `${s.id}:${s.n}`).join(',')}` : ''}`;
    if (!this.dirty && key === this.lastKey) return;
    this.dirty = false;
    this.lastKey = key;
    this.render();
  }

  private render(): void {
    const sys = warehouseOf(this.world);
    if (!sys) return;
    const pr = this.world.progress;
    const st = sys.view();
    const worn = new Set(Object.values(pr.equipment));

    // The bag: one cell per item kind (as the bag window shows it), worn ones marked and not movable.
    const counts = new Map<string, number>();
    for (const id of pr.inventory) counts.set(id, (counts.get(id) ?? 0) + 1);
    const bagGrid = h('div', { class: 'wh-grid' });
    for (const [id, n] of counts) {
      const it = itemOf(pr.rolls, id);
      if (!it) continue;
      const isWorn = worn.has(id);
      const loose = n - (isWorn ? 1 : 0);
      const c = this.cell(it, loose > 0 ? loose : n, 'bag', id, isWorn && loose <= 0);
      if (isWorn) c.append(h('i', {}, 'สวม'));
      c.addEventListener('click', (e) => {
        if (loose <= 0) return;
        this.send({ t: 'whDeposit', id, n: e.shiftKey ? 1 : loose });
      });
      bagGrid.append(c);
    }
    for (let k = counts.size; k < BAG_SIZE; k++) bagGrid.append(h('div', { class: 'wh-cell empty' }));

    const storeGrid = h('div', { class: 'wh-grid' });
    st.items.forEach((s, i) => {
      const it = itemOf(pr.rolls, s.id);
      if (!it) return;
      const c = this.cell(it, s.n, 'store', String(i), false);
      c.addEventListener('click', (e) => this.send({ t: 'whWithdraw', slot: i, n: e.shiftKey ? 1 : s.n }));
      c.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        if (s.n > 1) this.send({ t: 'whSplit', slot: i, n: Math.floor(s.n / 2) });
      });
      storeGrid.append(c);
    });
    for (let k = st.items.length; k < st.size; k++) storeGrid.append(h('div', { class: 'wh-cell empty' }));

    const bag = this.side('bag', T.bag, `${pr.inventory.length}/${BAG_SIZE} ช่อง`, bagGrid);
    const store = this.side('store', T.store, `${st.items.length}/${st.size} ช่อง`, storeGrid);

    const amount = h('input', { type: 'number', min: '1', step: '1', value: '100', ariaLabel: 'จำนวนตำลึง' });
    amount.addEventListener('keydown', (e) => e.stopPropagation());
    const val = () => Math.max(0, Math.floor(Number(amount.value) || 0));
    const btn = (label: string, on: () => void, main = false) => {
      const b = h('button', { type: 'button', class: `wh-btn${main ? ' main' : ''}` }, label);
      b.addEventListener('click', on);
      return b;
    };
    const foot = h(
      'div',
      { class: 'wh-foot' },
      h(
        'div',
        { class: 'wh-gold' },
        h('span', {}, `${T.gold}`),
        h('b', {}, st.gold.toLocaleString('th-TH')),
        h('small', {}, `· ติดตัว ${pr.gold.toLocaleString('th-TH')}`),
        amount,
        btn(T.goldIn, () => val() && this.send({ t: 'whGold', gold: val() }), true),
        btn(T.goldOut, () => val() && this.send({ t: 'whGold', gold: -val() })),
      ),
      btn(T.tidy, () => this.send({ t: 'whTidy' })),
      h('span', { class: 'wh-hint' }, T.hint),
    );

    const close = closeButton(() => {
      this.send({ t: 'whClose' });
      this.hide();
    });
    this.body.replaceChildren(
      h(
        'div',
        { class: 'wh-head' },
        h('h3', {}, T.name),
        h('small', {}, 'คลังเดียว ใช้ร่วมกันทุกเมือง'),
        close,
      ),
      h('div', { class: 'wh-cols' }, bag, store),
      foot,
    );
    this.body.style.display = 'contents';
  }

  private cell(it: NonNullable<ReturnType<typeof itemOf>>, n: number, from: From, key: string, locked: boolean): HTMLElement {
    const c = h(
      'button',
      { type: 'button', class: `wh-cell r-${it.rarity}`, title: itemName(it), draggable: !locked },
      iconEl(itemIcon(it)),
      n > 1 ? h('b', {}, String(n)) : null,
    );
    c.addEventListener('dragstart', (e) => {
      this.drag = { from, key };
      e.dataTransfer?.setData('text/plain', key);
    });
    c.addEventListener('dragend', () => (this.drag = null));
    return c;
  }

  private side(to: From, title: string, count: string, grid: HTMLElement): HTMLElement {
    const s = h('section', { data: { side: to } }, h('div', { class: 'wh-sh' }, h('h4', {}, title), h('small', {}, count)), grid);
    s.addEventListener('dragover', (e) => {
      if (!this.drag || this.drag.from === to) return;
      e.preventDefault();
      s.classList.add('drop');
    });
    s.addEventListener('dragleave', () => s.classList.remove('drop'));
    s.addEventListener('drop', (e) => {
      e.preventDefault();
      s.classList.remove('drop');
      const d = this.drag;
      this.drag = null;
      if (!d || d.from === to) return;
      if (d.from === 'bag') {
        const pr = this.world.progress;
        const n = pr.inventory.filter((x) => x === d.key).length - (Object.values(pr.equipment).includes(d.key) ? 1 : 0);
        if (n > 0) this.send({ t: 'whDeposit', id: d.key, n });
      } else this.send({ t: 'whWithdraw', slot: Number(d.key) });
    });
    return s;
  }
}
